import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useFieldTranslationKey } from 'components/common-form/hooks/useFieldTranslationKey';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { ButtonsGroup } from 'components/shared/ButtonsGroup/ButtonsGroup';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import type { IntlKey } from 'locales';
import { AvailableItems } from 'models/forms/fields/AvailableItemsField';
import type { RadioButtonsFieldData, RadioButtonsFieldMetadata } from 'models/forms/fields/RadioButtonsFieldMetadata';
import { FC, useCallback } from 'react';
import type { WorkspaceType } from 'state/workspaces/model';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';

export const RadioButtonsField: FC<ProductNodeBuilderProps> = ({ node: { id }, workspaceType, tabIndex }) => {
  const { availableItems = [], isReadOnly } = useFieldMetadata<RadioButtonsFieldMetadata>(id)!;
  const [value, setValue] = useData<RadioButtonsFieldData>(id);
  return (
    <RadioButtons
      id={id}
      workspaceType={workspaceType}
      availableItems={availableItems}
      value={value}
      setValue={setValue}
      isReadOnly={isReadOnly}
      tabIndex={tabIndex}
    />
  );
};

RadioButtonsField.displayName = 'RadioButtonsField';

interface RadioButtonsProps {
  id: string;
  workspaceType?: WorkspaceType;
  availableItems: AvailableItems;
  value: string | null;
  setValue: (value: string) => void;
  isReadOnly: boolean | undefined;
  tooltipKey?: IntlKey;
  tabIndex?: number;
}

export function RadioButtons({
  id,
  workspaceType,
  availableItems,
  value,
  setValue,
  isReadOnly,
  tooltipKey,
  tabIndex,
}: RadioButtonsProps) {
  const translationKey = useFieldTranslationKey(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);
  const onSelect = useCallback(
    (items: readonly string[]) => {
      const newValue = items[0];
      if (newValue !== value) {
        setValue(newValue);
      }
    },
    [setValue, value],
  );

  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${translationKey}` as IntlKey}
      workspaceType={workspaceType}
      tooltipKey={tooltipKey}
      className={patchedFieldClassName}
    >
      <ButtonsGroup
        className="w-100 h-100"
        items={availableItems?.map(x => x.value) ?? []}
        selectedItems={value}
        onSelect={onSelect}
        translationPrefix={`form.${id}`}
        isDisabled={isReadOnly}
        isRadio
        tabIndex={tabIndex}
        id={id}
      />
    </BlockOrientationFormGroup>
  );
}
